/* eslint-disable @typescript-eslint/no-explicit-any */
import { core } from '@tock/coretypes';
import { oc } from 'ts-optchain';

import { clone, findIndex, omit, pick } from 'es-toolkit/compat';
import { sortOptionsAndOptionGroups } from 'utils/ticketUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  TicketType,
  TicketOption,
  TicketOptionGroup,
  TicketTypeOptionGroupCopyRequest,
  TicketTypeOptionCopyRequest,
} = protos;

/* Begin new actions for supplements / supplement groups.
 * The searches for the supplement / supplement groups on the provided
 * ticket is in order to limit the possibility of an accidental
 * mutation to the supplement or supplement group then getting saved
 * to the server. The handler is really powerful so limiting the client
 * API is an attempt to protect us against accidental writes.
 */
export function addSupplement(
  ticket: core.ITicketType,
  existingSupplementGroup: core.ITicketOptionGroup,
  newSupplement: core.ITicketOption
): ApiAction {
  const expectedGroupId = existingSupplementGroup.id;
  const foundGroupIndex = findIndexOfEntityByIdWithinProto(ticket, 'optionGroup', expectedGroupId);
  const foundGroup = (ticket.optionGroup ?? [])[foundGroupIndex];
  const groupOptions = foundGroup.ticketOption ?? [];
  const constructedOption: core.ITicketOption = {
    ...newSupplement,
    ordering: groupOptions.length,
    ticketTypeId: ticket.id ? [ticket.id] : [],
    businessTax: newSupplement.businessTax,
  };

  const newSupplementGroup = {
    ...foundGroup,
    ticketOption: groupOptions.concat([constructedOption]),
  };
  const newOptionGroups = updateArrayAtIndex(
    ticket.optionGroup,
    foundGroupIndex,
    newSupplementGroup
  );

  return updateTicketTypeWithNewSupplementGroups(ticket, newOptionGroups);
}

export function archiveSupplement(
  ticket: any,
  existingSupplementGroup: any,
  existingSupplement: any,
  newSupplement: any
): ApiAction {
  const limitedObject = pick(newSupplement, ['isArchived']);
  return updateSupplementInternal(
    ticket,
    existingSupplementGroup,
    existingSupplement,
    limitedObject
  );
}

export function updateSupplement(
  ticket: core.TicketType,
  existingSupplementGroup: core.TicketOptionGroup,
  existingSupplement: core.TicketOption,
  newSupplement: core.ITicketOption
): ApiAction {
  return updateSupplementInternal(
    ticket,
    existingSupplementGroup,
    existingSupplement,
    omit(newSupplement, [
      'menuCategoryId',
      'currentQuantity',
      'quantityLimitType',
      'ordering',
      'isArchived',
      'ticketTypeId',
      'ticketOptionGroupId',
    ])
  );
}

function updateSupplementInternal(
  ticket: any,
  existingSupplementGroup: any,
  existingSupplement: any,
  newSupplement: any
) {
  const expectedGroupId = existingSupplementGroup.id;
  const foundGroupIndex = findIndexOfEntityByIdWithinProto(ticket, 'optionGroup', expectedGroupId);
  const foundGroup = ticket.optionGroup[foundGroupIndex];

  const expectedSupplementId = existingSupplement.id;
  const foundSupplementIndex = findIndexOfEntityByIdWithinProto(
    foundGroup,
    'ticketOption',
    expectedSupplementId
  );
  const foundSupplement = foundGroup.ticketOption[foundSupplementIndex];

  const constructedOption = TicketOption.fromObject({
    ...foundSupplement,
    ...newSupplement,
  });
  const newSupplementGroup = TicketOptionGroup.fromObject({
    ...foundGroup,
    ticketOption: updateArrayAtIndex(
      foundGroup.ticketOption,
      foundSupplementIndex,
      constructedOption
    ),
  });
  const newOptionGroups = updateArrayAtIndex(
    ticket.optionGroup,
    foundGroupIndex,
    newSupplementGroup
  );
  return updateTicketTypeWithNewSupplementGroups(ticket, newOptionGroups);
}

export function reorderSupplements(
  experience: core.TicketType,
  supplementGroup: core.ITicketOptionGroup,
  supplements: core.ITicketOption[],
  draggedItemIndex: number,
  hoveredItemIndex: number
): BaseTockAction {
  return {
    type: AppConstants.SUPPLEMENTS_REORDER,
    payload: {
      draggedItemIndex,
      experience,
      hoveredItemIndex,
      supplements,
      supplementGroup,
    },
  };
}

export function reorderSupplementModifiers(
  modifiers: core.TicketOptionGroup[],
  draggedItemIndex: number,
  hoveredItemIndex: number
): BaseTockAction {
  return {
    type: AppConstants.SUPPLEMENT_MODIFIERS_REORDER,
    payload: { modifiers, draggedItemIndex, hoveredItemIndex },
  };
}

export function addSupplementGroup(
  ticket: core.TicketType,
  newSupplementGroup: core.ITicketOptionGroup
): ApiAction {
  const constructedOptionGroup: core.ITicketOptionGroup = {
    name: newSupplementGroup.name,
    description: newSupplementGroup.description,
    ordering: ticket.optionGroup.length,
    optionGroupType: newSupplementGroup.optionGroupType,
    hasExclusions: newSupplementGroup.hasExclusions,
    isArchived: false,
    isRequired: newSupplementGroup.isRequired,
    ticketOption: newSupplementGroup.ticketOption,
    fixedLimitMin: newSupplementGroup.fixedLimitMin,
    fixedLimitMax: newSupplementGroup.fixedLimitMax,
  };

  const newOptionGroups = ticket.optionGroup.concat([constructedOptionGroup]);
  return updateTicketTypeWithNewSupplementGroups(ticket, newOptionGroups);
}

export function copySupplementGroup(copyRequest: core.TicketTypeOptionGroupCopyRequest): ApiAction {
  return {
    TockApi: {
      postProto: `/ticket/type/option/group/copy`,
      request: TicketTypeOptionGroupCopyRequest.fromObject(copyRequest),
      toast: 'Add-on group copied',
      type: AppConstants.SUPPLEMENT_GROUP_COPY,
    },
  };
}

export function copySupplement(copyRequest: core.TicketTypeOptionCopyRequest): ApiAction {
  return {
    TockApi: {
      postProto: `/ticket/type/option/copy`,
      request: TicketTypeOptionCopyRequest.fromObject(copyRequest),
      toast: 'Add-on copied',
      type: AppConstants.SUPPLEMENT_COPY,
    },
  };
}

export function archiveSupplementGroup(
  ticket: any,
  existingSupplementGroup: any,
  newSupplementGroup: any
): ApiAction {
  const limitedObject = pick(newSupplementGroup, ['isArchived']);
  return updateSupplementGroupInternal(ticket, existingSupplementGroup, limitedObject);
}

export function updateSupplementGroup(
  ticket: core.TicketType,
  existingSupplementGroup: core.ITicketOptionGroup,
  newSupplementGroup: core.ITicketOptionGroup
): ApiAction {
  const limitedObject = pick(newSupplementGroup, [
    'description',
    'hasExclusions',
    'isRequired',
    'name',
    'optionGroupType',
    'fixedLimitMin',
    'fixedLimitMax',
  ]);
  return updateSupplementGroupInternal(ticket, existingSupplementGroup, limitedObject);
}

function updateSupplementGroupInternal(
  ticket: core.TicketType,
  existingSupplementGroup: core.ITicketOptionGroup,
  newSupplementGroup: core.ITicketOptionGroup
) {
  const expectedGroupId = existingSupplementGroup.id;
  const foundGroupIndex = findIndexOfEntityByIdWithinProto(ticket, 'optionGroup', expectedGroupId);

  const foundGroup = ticket.optionGroup[foundGroupIndex];
  const newGroup = {
    ...foundGroup,
    ...newSupplementGroup,
  };
  const newOptionGroups = updateArrayAtIndex(ticket.optionGroup, foundGroupIndex, newGroup);
  return updateTicketTypeWithNewSupplementGroups(ticket, newOptionGroups);
}

export function reorderSupplementGroups(
  experience: core.TicketType,
  supplementGroups: core.ITicketOptionGroup[],
  draggedItemIndex: number,
  hoveredItemIndex: number
): BaseTockAction {
  return {
    type: AppConstants.SUPPLEMENT_GROUPS_REORDER,
    payload: {
      draggedItemIndex,
      experience,
      hoveredItemIndex,
      supplementGroups,
    },
  };
}
// End new actions for supplements / supplement groups

export function upsertSupplementModifier(
  ticketType: core.TicketType,
  modifier: core.ITicketOptionGroup
) {
  return {
    TockApi: {
      postProto: '/ticket/option/group',
      request: TicketOptionGroup.fromObject({
        ...modifier,
        ticketTypeId: ticketType.id,
      }),
      toast: 'Modifier saved',
      type: AppConstants.SUPPLEMENT_MODIFIER_UPSERT,
    },
  };
}

export function upsertTicketOptionInventory(
  ticketType: core.TicketType,
  { id, name, currentQuantity, quantityLimitType }: core.ITicketOption
) {
  return {
    TockApi: {
      postProto: '/ticket/option/inventory',
      request: TicketOption.fromObject({
        id,
        name,
        currentQuantity,
        quantityLimitType,
        ticketTypeId: [ticketType.id],
      }),
      toast: 'Add-on inventory saved',
      type: AppConstants.SUPPLEMENT_MODIFIER_UPSERT,
    },
  };
}

export function upsertTicketOptionMenuCategory(
  ticketType: core.TicketType,
  { id, name, menuCategoryId }: core.ITicketOption
) {
  return {
    TockApi: {
      postProto: '/ticket/option/menu/category',
      request: TicketOption.fromObject({
        id,
        name,
        menuCategoryId,
        ticketTypeId: [ticketType.id],
      }),
      toast: 'Add-on category saved',
      type: AppConstants.SUPPLEMENT_MODIFIER_UPSERT,
    },
  };
}

// Universal update function for supplement groups
function updateTicketTypeWithNewSupplementGroups(ticket: any, optionGroups: any) {
  const updatedTicket = sortOptionsAndOptionGroups({
    ...ticket,
    optionGroup: optionGroups,
  });
  const request = TicketType.fromObject(updatedTicket);
  return {
    TockApi: {
      putProto: '/ticket/type',
      request,
      toast: 'Experience updated',
      type: AppConstants.TICKETS_UPDATE,
    },
  };
}

function findIndexOfEntityByIdWithinProto(
  containingProto: any,
  keyOfList: any,
  expectedId: number | undefined | null
) {
  const foundIndex = findIndex((oc(containingProto) as any)[keyOfList]([]), (entity: any) => {
    if (!entity.id) {
      return false;
    }
    return entity.id === expectedId;
  });

  if (foundIndex < 0) {
    throw new Error(`Couldn't find ${expectedId} in list of ${keyOfList}`);
  }
  return foundIndex;
}

function updateArrayAtIndex(array: any, index: any, newValue: any) {
  const arrCopy = clone(array);
  arrCopy[index] = newValue;
  return arrCopy;
}
